import { useEffect, useState, type ReactNode } from 'react'

// A paged, sortable grid over an API that pages server-side. The first grid in the app that
// does (Foundation and the dashboards load whole result sets). Membership tab, PR A.

// "Show All" sends this as the page size; the API caps size at the same value, so one page
// holds the whole result set (used on the Interactions grid).
export const GRID_ALL_SIZE = 100000

export interface Column<T> {
  key: string
  label: string
  sortable?: boolean
  align?: 'right' | 'center'
  className?: string
  title?: string
  render?: (row: T) => ReactNode
}

export interface DataGridProps<T> {
  columns: Column<T>[]
  rows: T[]
  total: number
  page: number
  size: number
  sort: string
  dir: 'asc' | 'desc'
  loading?: boolean
  error?: string | null
  rowKey: (row: T) => string | number
  rowClass?: (row: T) => string
  onSort: (key: string, dir: 'asc' | 'desc') => void
  onPage: (page: number) => void
  onSize: (size: number) => void
  onRowClick?: (row: T) => void
  emptyText?: string
  sizes?: number[]
  allowAll?: boolean
}

export function DataGrid<T>({ columns, rows, total, page, size, sort, dir, loading, error, rowKey, rowClass, onSort, onPage, onSize, onRowClick, emptyText = 'Nothing matches.', sizes = [30, 60, 100], allowAll = false }: DataGridProps<T>) {
  const pages = Math.max(1, Math.ceil(total / size))
  const from = total === 0 ? 0 : (page - 1) * size + 1
  const to = Math.min(total, page * size)
  const [pageInput, setPageInput] = useState(String(page))
  useEffect(() => { setPageInput(String(page)) }, [page])

  const header = (c: Column<T>) => {
    const sortable = c.sortable !== false
    const active = sort === c.key
    const cls = [c.align === 'right' ? 'ta-right' : '', sortable ? 'mbr-th-sort' : '', active ? 'mbr-th-active' : '', c.className ?? ''].filter(Boolean).join(' ')
    const next: 'asc' | 'desc' = active && dir === 'asc' ? 'desc' : 'asc'
    return (
      <th key={c.key} className={cls} title={c.title ?? (sortable ? `Sort by ${c.label.toLowerCase()}` : undefined)}
          onClick={sortable ? () => onSort(c.key, next) : undefined} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}>
        {c.label}{active && <span className="mbr-sort-ind">{dir === 'asc' ? ' ▲' : ' ▼'}</span>}
      </th>
    )
  }

  return (
    <div className="mbr-grid">
      <div className="mbr-table-wrap">
        <table className="hc-table">
          <thead><tr>{columns.map(header)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={rowKey(r)} className={[onRowClick ? 'mbr-row-link' : '', rowClass ? rowClass(r) : ''].filter(Boolean).join(' ')} onClick={onRowClick ? () => onRowClick(r) : undefined}>
                {columns.map((c) => (
                  <td key={c.key} className={[c.align === 'right' ? 'ta-right' : '', c.className ?? ''].filter(Boolean).join(' ')}>
                    {c.render ? c.render(r) : String((r as Record<string, unknown>)[c.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={columns.length} className="mbr-empty">{error ? <span className="mbr-error">{error}</span> : emptyText}</td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="mbr-loading">Loading…</div>}
      </div>
      <div className="mbr-pager">
        <span className="mbr-count">{total === 0 ? 'No records' : `Showing ${from.toLocaleString('en-GB')}–${to.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')}`}</span>
        <span className="mbr-spacer" />
        <label className="mbr-pager-size">Per page
          <select value={size} onChange={(e) => onSize(Number(e.target.value))}>
            {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
            {allowAll && <option value={GRID_ALL_SIZE}>All</option>}
          </select>
        </label>
        <button type="button" className="btn-secondary mbr-pbtn" disabled={page <= 1} onClick={() => onPage(1)} title="First page">«</button>
        <button type="button" className="btn-secondary mbr-pbtn" disabled={page <= 1} onClick={() => onPage(page - 1)} title="Previous page">‹</button>
        <span className="mbr-pager-page">Page
          <input type="number" min={1} max={pages} value={pageInput} onChange={(e) => setPageInput(e.target.value)}
                 onBlur={() => { const n = Math.min(pages, Math.max(1, Number(pageInput) || 1)); if (n !== page) onPage(n); else setPageInput(String(page)) }}
                 onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }} />
          of {pages.toLocaleString('en-GB')}
        </span>
        <button type="button" className="btn-secondary mbr-pbtn" disabled={page >= pages} onClick={() => onPage(page + 1)} title="Next page">›</button>
        <button type="button" className="btn-secondary mbr-pbtn" disabled={page >= pages} onClick={() => onPage(pages)} title="Last page">»</button>
      </div>
    </div>
  )
}
